import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import cx from 'classnames';

import { getFormInputClasses, setErrorWidth, getDropdownDefaultOption } from '../../utilities/form-helpers';
import { shouldMonthComeFirst } from '../../intl/utils';
import {
  generateDayDropdownItems,
  generateMonthDropdownItems,
  generateMonthNumbersDropdownItems,
  generateYearDropdownItems,
} from '../../utilities/dropdown-generators';

const DAY_OPTIONS = generateDayDropdownItems();

import Dropdown from './dropdown';
import FormInputCorrectMessage from './form-input-correct-message';
import FormInputErrorMessage from './form-input-error-message';
import FormInputLabel from './form-input-label';

const AUTOCOMPLETE_NAMES = {
  bday: {
    month: 'bday-month',
    day: 'bday-day',
    year: 'bday-year',
  },
};

class FormInputDateDropdown extends Component {
  static defaultProps = {
    checkIcon: true,
    isDisabled: false,
    isHidden: false,
    isRequired: false,
    monthsInNumbers: false,
    shouldSetErrorWidth: false,
    setStateOnUpdate: false,
    onFocusDate: false,
  }

  constructor() {
    super();

    this.state = {
      defaultDayOption: null,
      defaultMonthOption: null,
      defaultYearOption: null,
      errorWidth: null,
      selectedDay: '',
      selectedMonth: '',
      selectedYear: '',
    };

    this.onDaySelected = this.onDaySelected.bind(this);
    this.onFocusDate = this.onFocusDate.bind(this);
    this.onMonthSelected = this.onMonthSelected.bind(this);
    this.onYearSelected = this.onYearSelected.bind(this);
    this.question = null;
  }

  componentWillMount() {
    const {
      input: {
        onBlur,
        value,
      },
      monthsInNumbers,
      meta: { touched },
      range,
    } = this.props;

    // this set of options needs to be generated based on given props, so it can be used as both
    // birthday field and expiration date field.
    // values are related to current year, -1 includes current year.
    const min = range ? range.min : -1;
    const max = range ? range.max : 99;

    this.monthOptions = monthsInNumbers ? generateMonthNumbersDropdownItems() : generateMonthDropdownItems();
    this.yearOptions = generateYearDropdownItems(min, max);

    // has to be after month and year options generated
    this.setStateFromValueProp(this.props, true);

    // If field has an initial value set, call onBlur so field
    // is 'touched', and validation state will be visible
    if (value && !touched) {
      onBlur();
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      setStateOnUpdate,
      input: { value },
    } = this.props;

    if (value !== nextProps.input.value) {
      this.setStateFromValueProp(nextProps, setStateOnUpdate);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      meta: {
        pristine,
        valid,
      },
      shouldSetErrorWidth,
    } = this.props;

    const {
      errorWidth,
      selectedDay,
      selectedMonth,
      selectedYear,
    } = this.state;

    const newDate = `${selectedYear}-${this.formatSelectedNumber(selectedMonth)}-${this.formatSelectedNumber(selectedDay)}`;
    const prevDate = `${prevState.selectedYear}-${this.formatSelectedNumber(prevState.selectedMonth)}-${this.formatSelectedNumber(prevState.selectedDay)}`;
    const allFieldsSelected = selectedDay && selectedMonth && selectedYear;

    if ((allFieldsSelected || valid) // all fields have a valid number
      && newDate !== prevDate // date has been updated
      && (!pristine || newDate.length === 10)) {
      this.handleDateChange(newDate);
    } else if ((selectedDay && !selectedMonth && !selectedYear)) {
      this.handleDateChange(selectedDay);
    } else if ((!selectedDay && selectedMonth && !selectedYear)) {
      this.handleDateChange(selectedMonth);
    } else if ((!selectedDay && !selectedMonth && selectedYear)) {
      this.handleDateChange(selectedYear);
    }
  
    // Set error width (prevents error message being truncated)
    if (this.formInputErrorDOM && shouldSetErrorWidth) {
      const newErrorWidth = setErrorWidth(this.formInputErrorDOM.getBoundingClientRect().left);

      if (newErrorWidth !== errorWidth) {
        this.setState({
          errorWidth: newErrorWidth,
        });
      }
    }
  }

  render() {
    const {
      checkIcon,
      className,
      content,
      input: { value, name },
      inputClassModifier,
      isDisabled,
      isHidden,
      isRequired,
      label,
      labelClassModifier,
      labelCopyID,
      meta: {
        error,
        touched,
        valid,
      },
      showOptionalLabel,
      storefront,
      withDivider,
    } = this.props;

    const { 
      errorWidth,
      onFocusDate,
    } = this.state;

    const isError = error && touched;
    const isValid = valid && touched;
    const monthFirst = shouldMonthComeFirst(storefront);
    const formFieldClasses = cx({
      'FormInput-wrapper': true,
      'FormInputDateDropdown': true,
      'is-disabled': isDisabled,
      'is-error': isError,
      'is-correct': isValid,
      'withDivider': withDivider,
      'onFocusDate' : onFocusDate,
    }, className);

    const containerFieldClasses = cx({
      'FormInputDateDropdown-wrapper': true,
      'withDivider': withDivider,
    }, className);
    this.question = {
      yearB: 
      <div className={containerFieldClasses}>
        {this.renderYear()}
      </div>,
      dayB:
      <div className={containerFieldClasses}>
        {this.renderDay()}
      </div>,
      monthB:
      <div className={containerFieldClasses}>
        {this.renderMonth()}
      </div>,
    };

    return (
      <div className={ getFormInputClasses(inputClassModifier, isHidden) }>
        <div className={ formFieldClasses } >
          <FormInputLabel
            isRequired={ isRequired }
            label={ label }
            labelClassModifier={ labelClassModifier }
            labelCopyID={ labelCopyID }
            name={ name }
            showOptionalLabel={ showOptionalLabel }
          />
          { content ? 
            this.question[content]
            : 
            <div className={containerFieldClasses}>
              { monthFirst ? this.renderMonth() : this.renderDay() }
              { this.renderSeparator() }
              { monthFirst ? this.renderDay() : this.renderMonth() }
              { this.renderSeparator() }
              { this.renderYear() }
            </div>
          }
          { isError ?
            <FormInputErrorMessage
              error={ error }
              errorWidth={ errorWidth }
              name={ name }
              ref={ (c) => this.formInputErrorDOM = ReactDOM.findDOMNode(c) }
            /> : null }
          { (isValid && checkIcon) && <FormInputCorrectMessage value={ value } /> }
        </div>
      </div>
    );
  }

  renderDay() {
    const {
      autocompleteGroupName,
      content,
      helperTraslatedCopy,
    } = this.props;
    
    const { defaultDayOption } = this.state;
    const autocompleteName = AUTOCOMPLETE_NAMES[autocompleteGroupName] ? `${autocompleteGroupName} ${AUTOCOMPLETE_NAMES[autocompleteGroupName].day}` : null;
    const placeholder = helperTraslatedCopy.getFormatMessageFromcopyId('DD_PLACEHOLDER') !== '' 
      && content === undefined ? helperTraslatedCopy.getFormatMessageFromcopyId('DD_PLACEHOLDER') : content !== '' 
      && helperTraslatedCopy.getFormatMessageFromcopyId('APP_SELECT');
    
    return (
      <Dropdown
        autocompleteName={ autocompleteName }
        className='FormInputDateDropdown-dropdown FormInputDateDropdown-dropdown--daySelect'
        defaultOption={ content !== '' ? 'defaultDayOption' : defaultDayOption }
        helperTraslatedCopy={ helperTraslatedCopy }
        inputDateCp={ true }
        isDisabled={ false }
        isPristine={ true }
        isRequired={ false }
        name={ autocompleteName ? autocompleteName.replace(/\s/g, '.') : '' }
        onFocusDate= { this.onFocusDate }
        onSelect={ this.onDaySelected }
        options={ DAY_OPTIONS }
        placeholder={ placeholder }
      />
    );
  }

  renderMonth() {
    const {
      autocompleteGroupName,
      content,
      helperTraslatedCopy,
    } = this.props;

    const { 
      defaultMonthOption, 
    } = this.state;
    
    const autocompleteName = AUTOCOMPLETE_NAMES[autocompleteGroupName] ?
      `${autocompleteGroupName} ${AUTOCOMPLETE_NAMES[autocompleteGroupName].month}` : null;

    const placeholderCopy = helperTraslatedCopy.getFormatMessageFromcopyId('MM_PLACEHOLDER') !== '' 
      && content === undefined ? helperTraslatedCopy.getFormatMessageFromcopyId('MM_PLACEHOLDER') : content !== '' 
      && helperTraslatedCopy.getFormatMessageFromcopyId('APP_SELECT');

    return (
      <Dropdown
        autocompleteName={ autocompleteName }
        className='FormInputDateDropdown-dropdown FormInputDateDropdown-dropdown--monthSelect'
        defaultOption={ content !== '' ? 'defaultMonthOption' : defaultMonthOption }
        inputDateCp={ true }
        isDisabled={ false }
        isPristine={ true }
        isRequired={ false }
        name={ autocompleteName ? autocompleteName.replace(/\s/g, '.') : '' }
        onFocusDate= { this.onFocusDate }
        onSelect={ this.onMonthSelected }
        options={ content ? this.monthOptionsByNumber : this.monthOptions }
        placeholder={ placeholderCopy }
      />
    );
  }

  renderYear() {
    const { 
      autocompleteGroupName, 
      content, 
      helperTraslatedCopy, 
    } = this.props;

    const { 
      defaultYearOption, 
    } = this.state;

    const autocompleteName = AUTOCOMPLETE_NAMES[autocompleteGroupName] ?
      `${autocompleteGroupName} ${AUTOCOMPLETE_NAMES[autocompleteGroupName].year}` : null;

    const placeholder = helperTraslatedCopy.getFormatMessageFromcopyId('YYYY_PLACEHOLDER') !== '' 
      && content === undefined ? helperTraslatedCopy.getFormatMessageFromcopyId('YYYY_PLACEHOLDER') : content !== '' 
      && helperTraslatedCopy.getFormatMessageFromcopyId('APP_SELECT');

    return (
      <Dropdown
        autocompleteName={ autocompleteName }
        className='FormInputDateDropdown-dropdown FormInputDateDropdown-dropdown--yearSelect'
        defaultOption={ content !== '' ? 'defaultYearOption' : defaultYearOption}
        inputDateCp={ true }
        isDisabled={ false }
        isPristine={ true }
        isRequired={ false }
        onFocusDate= { this.onFocusDate }
        onSelect={ this.onYearSelected }
        options={ this.yearOptions }
        placeholder={ placeholder }
      />
    );
  }

  renderSeparator() {
    const {
      withDivider,
    } = this.props;

    return withDivider && <p > { '/' }</p>;
  }

  /**
   * The following function is a callback function from dropdown and expect:
   * onSelect(newSelectedIndex, newSelectedOption, prevIndex, prevOption)
   * and since only the second parameter is used by this component, the rest operator is used.
   * @param args
   */
  onDaySelected(...args) {
    this.setState({
      selectedDay: args[1],
    });
  }

  onFocusDate(value) {
    this.setState({
      onFocusDate:value,
    });
  }
  /**
   * The following function is a callback function from dropdown and expect:
   * onSelect(newSelectedIndex, newSelectedOption, prevIndex, prevOption)
   * and since only the second parameter is used by this component, the rest operator is used.
   * @param args
   */
  onMonthSelected(...args) {
    this.setState({
      selectedMonth: args[1],
    });
  }
  /**
   * The following function is a callback function from dropdown and expect:
   * onSelect(newSelectedIndex, newSelectedOption, prevIndex, prevOption)
   * and since only the second parameter is used by this component, the rest operator is used.
   * @param args
   */
  onYearSelected(...args) {
    this.setState({
      selectedYear: args[1],
    });
  }

  handleDateChange(newDate) {
    const { input: { onBlur } } = this.props;

    onBlur(newDate);
  }

  /**
   * @param {Object} props
   * @param {Boolean} isMounting - method fired during willMount
   */
  setStateFromValueProp(props, isMounting) {
    const { input: { value } } = props;
    if (value && typeof value === 'string') {
      const selectedDate = value.split('-');
      const year = parseInt(selectedDate[0], 10);
      const month = parseInt(selectedDate[1], 10);
      const day = parseInt(selectedDate[2], 10);

      this.setState({
        defaultDayOption: isMounting ? getDropdownDefaultOption(DAY_OPTIONS, day) : null,
        defaultMonthOption: isMounting ? getDropdownDefaultOption(this.monthOptions, month) : null,
        defaultYearOption: isMounting ? getDropdownDefaultOption(this.yearOptions, year) : null,
        selectedDay: day,
        selectedMonth: month,
        selectedYear: year,
      });
    }
  }

  formatSelectedNumber(number) {
    return number.toString().length === 2 ? number : `0${number}`;
  }
}

export default FormInputDateDropdown;
